import React from "react";
import { Group, Line, Label, Tag, Text } from "react-konva";
import { formattedColorMaterial } from "../formatted/FormattedText";
import { useTranslation } from "react-i18next";

const WastePolygon = React.memo(function WastePolygon({
  anomalie,
  isHovered,
  isVisible = false,
  setHoveredWasteObjectId = () => {},
  setSelectedAnomalie = () => {},
  isCharacterization = false,
  isActivity = false,
  canvasWidth,
  canvasHeight,
  canvasOffsetX = 0,
  canvasOffsetY = 0,
  scaleFactor = 1,
  renderLabelOnly = false,
}) {
  const { t } = useTranslation();
  const shouldDisplay = isVisible || isHovered;
  const points = anomalie.points_konva;

  const center = points.reduce(
    (acc, val, idx) => {
      if (idx % 2 === 0)
        acc.x += val; // x
      else acc.y += val; // y
      return acc;
    },
    { x: 0, y: 0 },
  );
  center.x /= points.length / 2;
  center.y /= points.length / 2;

  // Calculate label position to avoid clipping at canvas edges
  const getLabelPosition = () => {
    if (!canvasWidth || !canvasHeight) {
      // Fallback to original position if dimensions not provided
      return { x: center.x + 50, y: center.y - 30 };
    }

    const labelText = t(anomalie.cls);
    // Estimate label dimensions (fontSize 16, padding 10)
    // Rough estimate: ~10px per character for fontSize 16
    const baseLabelWidth = labelText.length * 10 + 20; // text width + padding
    const baseLabelHeight = 36; // fontSize 16 + padding 10*2

    // Adjust for scale factor since label will be scaled inversely
    const estimatedLabelWidth = baseLabelWidth / scaleFactor;
    const estimatedLabelHeight = baseLabelHeight / scaleFactor;

    // Canvas boundaries in the coordinate system of the Group (image coordinates)
    const canvasMinX = canvasOffsetX;
    const canvasMaxX = canvasOffsetX + canvasWidth;
    const canvasMinY = canvasOffsetY;
    const canvasMaxY = canvasOffsetY + canvasHeight;

    // Default offset from center
    let labelX = center.x + 50;
    let labelY = center.y - 30;

    // Adjust X position if label would go off right edge
    if (labelX + estimatedLabelWidth > canvasMaxX) {
      // Try placing on the left side of the center
      labelX = center.x - estimatedLabelWidth - 50;
      // If still off left edge, position at left edge with margin
      if (labelX < canvasMinX + 10) {
        labelX = canvasMinX + 10;
      }
    } else if (labelX < canvasMinX + 10) {
      // If off left edge, position at left edge with margin
      labelX = canvasMinX + 10;
    }

    // Adjust Y position if label would go off bottom edge
    if (labelY + estimatedLabelHeight > canvasMaxY) {
      // Place above the center
      labelY = center.y - estimatedLabelHeight - 30;
      // If still off top edge, position at top edge with margin
      if (labelY < canvasMinY + 10) {
        labelY = canvasMinY + 10;
      }
    } else if (labelY < canvasMinY + 10) {
      // If off top edge, position at top edge with margin
      labelY = canvasMinY + 10;
    }

    return { x: labelX, y: labelY };
  };

  const labelPosition = getLabelPosition();

  const renderLabel = () =>
    isHovered ? (
      <Label
        x={labelPosition.x}
        y={labelPosition.y}
        scaleX={1 / scaleFactor}
        scaleY={1 / scaleFactor}
        listening={false}
      >
        <Tag
          fill="white"
          opacity={0.9}
          cornerRadius={8}
          shadowColor="black"
          shadowBlur={5}
          shadowOffset={{ x: 2, y: 2 }}
        />
        <Text
          text={t(anomalie.cls)}
          fontSize={16}
          fontStyle="bold"
          padding={10}
          fill="black"
        />
      </Label>
    ) : null;

  if (renderLabelOnly) {
    return renderLabel();
  }

  return (
    <Group key={anomalie.id}>
      <Group
        key={`anomaly-${anomalie.id}`}
        listening={true}
        onMouseEnter={(e) => {
          if (isCharacterization || isActivity) {
            setHoveredWasteObjectId(anomalie.id);
          }
        }}
        onMouseMove={(e) => {
          if (isCharacterization || isActivity) {
          }
        }}
        onMouseLeave={() => {
          if (isCharacterization || isActivity) {
            setHoveredWasteObjectId(null);
          }
        }}
        onClick={() => {
          if (isHovered && (isCharacterization || isActivity)) {
            setSelectedAnomalie(anomalie);
          }
        }}
      >
        <Line
          points={points}
          opacity={shouldDisplay ? (isHovered ? 0.8 : 0.3) : 0.01}
          strokeWidth={0}
          closed
          fill={formattedColorMaterial(anomalie.cls)}
          listening={true}
        />
        <Line
          points={points}
          opacity={shouldDisplay ? 1 : 0.01}
          strokeWidth={isHovered ? 8 : 5}
          closed
          stroke={formattedColorMaterial(anomalie.cls)}
          listening={true}
        />
      </Group>
    </Group>
  );
});

export default WastePolygon;
